import React from 'react';
import { useUser } from '../../../contexts/UserContext';
import { supabase } from '../../../lib/supabaseClient';
import { logger } from '../../../utils/logger';

/**
 * AuthDiagnostics - Development-only component to display authentication state
 * 
 * Shows:
 * - User email and ID
 * - Resolved role and source (jwt | db | cache | none)
 * - Workspace ID and ready status
 * - Load errors
 * 
 * Only visible in development mode (import.meta.env.DEV)
 */
export const AuthDiagnostics: React.FC = () => {
  const { user, role, userId, workspaceId, authReady } = useUser();
  const [roleSource, setRoleSource] = React.useState<'jwt' | 'db' | 'cache' | 'none'>('none');
  const [sessionRole, setSessionRole] = React.useState<string | null>(null);
  const [dbRole, setDbRole] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!import.meta.env.DEV) return;

    async function checkRoleSource() {
      try {
        const { data: session } = await supabase.auth.getSession();
        
        // Check JWT metadata
        const jwtRole = session?.session?.user?.user_metadata?.role || 
                       session?.session?.user?.app_metadata?.role;
        
        if (jwtRole) {
          setSessionRole(jwtRole);
          setRoleSource('jwt');
          return;
        }

        // Check database
        if (session?.session?.user?.id) {
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.session.user.id)
            .maybeSingle<{ role: string }>();
          
          if (roleData) {
            setDbRole(roleData.role);
            setRoleSource('db');
            return;
          }
        }

        // Check localStorage cache
        const cachedRole = localStorage.getItem('auth:role');
        if (cachedRole) {
          setRoleSource('cache');
          return;
        }

        setRoleSource('none');
      } catch (error) {
        logger.error('AuthDiagnostics: Error checking role source', { error }, 'AuthDiagnostics');
      }
    }

    checkRoleSource();
  }, [role, userId]);

  // Only show in development
  if (!import.meta.env.DEV) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50 max-w-md bg-gray-900 text-white text-xs p-3 rounded-lg shadow-lg border border-gray-700 font-mono">
      <div className="font-bold mb-2 text-yellow-400">🔍 Auth Diagnostics (Dev Only)</div>
      <div className="space-y-1">
        <div><span className="text-gray-400">Email:</span> {user?.email || 'null'}</div>
        <div><span className="text-gray-400">User ID:</span> {userId || 'null'}</div>
        <div><span className="text-gray-400">Role:</span> <span className={role === 'admin' ? 'text-green-400' : role === 'viewer' ? 'text-blue-400' : 'text-red-400'}>{role || 'null (waiting approval)'}</span></div>
        <div><span className="text-gray-400">Role Source:</span> <span className="text-yellow-400">{roleSource}</span></div>
        {sessionRole && <div><span className="text-gray-400">JWT Role:</span> {sessionRole}</div>}
        {dbRole && <div><span className="text-gray-400">DB Role:</span> {dbRole}</div>}
        <div><span className="text-gray-400">Workspace ID:</span> {workspaceId}</div>
        <div><span className="text-gray-400">Auth Ready:</span> {authReady ? '✅' : '⏳'}</div>
      </div>
    </div>
  );
};

